import { useState, useRef, useEffect } from "react";
import { DecryptedAvatar } from "./DecryptedAvatar";
import { linkify } from "./linkify";
import type { ChatMessage } from "../hooks/useChat";
import { useAutoResize } from "../hooks/useAutoResize";
import { formatChatTimestamp } from "../lib/dateFormat";

// Each member maps deterministically to one palette color, so a person keeps the
// same color across all their messages within a view (consistent on this device;
// may differ device-to-device, which is acceptable).
const SENDER_COLORS = [
  "#1d9bf0", "#17bf63", "#e0245e", "#f45d22", "#794bc4",
  "#ffad1f", "#e91e63", "#00bcd4", "#8bc34a", "#ff7043",
];
function colorForSender(id: number): string {
  return SENDER_COLORS[((id % SENDER_COLORS.length) + SENDER_COLORS.length) % SENDER_COLORS.length];
}


interface MemberAvatarMap {
  get(id: number): string | null | undefined;
}

interface ChatBoxProps {
  messages: ChatMessage[];
  memberAvatars: MemberAvatarMap;
  onSend: (text: string) => void;
  title?: string;
}

export function ChatBox({ messages, memberAvatars, onSend, title = "Chat" }: ChatBoxProps) {
  const [input, setInput] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the textarea to fit its content (soft-wrapping), capped by the
  // max-height in CSS, beyond which it scrolls.
  useAutoResize(inputRef, input);

  // Scroll the message list itself, not via scrollIntoView — the latter also
  // scrolls every scrollable ancestor (the page), clipping the header on load.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    onSend(text);
    setInput("");
  };

  return (
    <div className="chat-section">
      <h3 className="subsection-title">{title}</h3>

      <div className="chat-messages" ref={listRef}>
        {messages.map((msg) => (
          <div key={msg.id} className="chat-msg">
            <div className="chat-avatar-wrap">
              <DecryptedAvatar token={memberAvatars.get(msg.senderMemberId)} name={msg.senderName} size="tiny" />
            </div>
            <div className="chat-msg-body">
              <div className="chat-msg-header">
                <span className="chat-sender-name" style={{ color: colorForSender(msg.senderMemberId) }}>
                  {msg.senderName}
                </span>
                <span className="chat-msg-time">{formatChatTimestamp(msg.createdAt)}</span>
              </div>
              <span className="chat-text">{linkify(msg.text)}</span>
            </div>
          </div>
        ))}
      </div>

      <form
        className="chat-input-bar"
        onSubmit={(e) => { e.preventDefault(); handleSend(); }}
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type a message..."
          className="chat-input"
          aria-label="Message"
          rows={1}
        />
        <button type="submit" className="chat-send-btn" aria-label="Send message">
          <span className="material-icons" style={{ fontSize: "20px" }} aria-hidden="true">send</span>
        </button>
      </form>
    </div>
  );
}
